import React, { useEffect, useState } from 'react';
import { RoomIcon, ROOM_ICON_OPTIONS, getRoomIconLabel, normalizeRoomIconKey } from './roomIcons';
import './RoomFormModal.css';

const TYPE_KEYS = ['seedling', 'vegetation', 'flowering', 'drying'];

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(body: { name: string, typeKey: string, iconKey: string, description: string }) => Promise<void>} props.onSubmit
 * @param {object | null} [props.initial]
 * @param {string} [props.farmLabel]
 * @param {string} [props.farmId] — required for create (X-Farm-Context / farm scope)
 * @param {(k: string) => string} props.t
 * @param {boolean} [props.editing]
 */
export default function RoomFormModal({ open, onClose, onSubmit, initial, farmLabel, farmId, t, editing }) {
  const [name, setName] = useState('');
  const [typeKey, setTypeKey] = useState('');
  const [iconKey, setIconKey] = useState('leaf');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setErr('');
    if (initial) {
      setName(initial.name != null ? String(initial.name) : '');
      setTypeKey(initial.typeKey != null ? String(initial.typeKey) : '');
      setIconKey(initial.iconKey != null ? normalizeRoomIconKey(initial.iconKey) : 'leaf');
      setDescription(initial.description != null ? String(initial.description) : '');
    } else {
      setName('');
      setTypeKey('');
      setIconKey('leaf');
      setDescription('');
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setErr(t('roomForm.nameRequired') || 'Name is required');
      return;
    }
    if (!editing && !String(farmId || '').trim()) {
      setErr(t('roomForm.noFarmContext'));
      return;
    }
    setSaving(true);
    setErr('');
    try {
      await onSubmit({
        name: trimmed,
        typeKey: typeKey || '',
        iconKey: normalizeRoomIconKey(iconKey || 'leaf'),
        description: description.trim(),
      });
      onClose();
    } catch (er) {
      setErr(String(er?.message || er || t('roomForm.saveFailed') || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      role="dialog"
      aria-modal="true"
      dir="auto"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content shadow">
          <form onSubmit={handleSave}>
            <div className="modal-header">
              <h2 className="h5 mb-0">{editing ? t('roomForm.editTitle') : t('roomForm.createTitle')}</h2>
              <button
                type="button"
                className="btn-close"
                aria-label={t('roomForm.close')}
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              {err ? <div className="alert alert-danger py-2">{err}</div> : null}
              {farmLabel || farmId ? (
                <p className="text-muted small mb-2">
                  {t('roomForm.farmHint')}:{' '}
                  <span className="text-body">{farmLabel || farmId || '—'}</span>
                </p>
              ) : null}
              {farmId && !editing ? (
                <p className="small text-muted font-monospace mb-3" title={t('roomForm.farmIdTitle') || ''}>
                  {t('roomForm.farmIdLabel')}: {farmId}
                </p>
              ) : null}
              <div className="mb-3">
                <label className="form-label" htmlFor="room-form-name">
                  {t('roomForm.name')}
                </label>
                <input
                  id="room-form-name"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="room-form-type">
                  {t('roomForm.type')}
                </label>
                <select
                  id="room-form-type"
                  className="form-select"
                  value={typeKey}
                  onChange={(e) => setTypeKey(e.target.value)}
                >
                  <option value="">{t('roomForm.typeNone')}</option>
                  {TYPE_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {t(`roomType.${k}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label" id="room-form-icon-label">
                  {t('roomForm.icon')}
                </label>
                <div
                  className="room-icon-picker"
                  role="radiogroup"
                  aria-labelledby="room-form-icon-label"
                >
                  {ROOM_ICON_OPTIONS.map((option) => {
                    const selected = normalizeRoomIconKey(iconKey) === option.key;
                    const label = getRoomIconLabel(option.key, t);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        className={
                          'room-icon-picker__option' +
                          (selected ? ' room-icon-picker__option--selected' : '')
                        }
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setIconKey(option.key)}
                      >
                        <span className="room-icon-picker__badge" aria-hidden="true">
                          <RoomIcon iconKey={option.key} className="room-icon-picker__svg" />
                        </span>
                        <span className="room-icon-picker__label">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mb-0">
                <label className="form-label" htmlFor="room-form-desc">
                  {t('roomForm.description')}
                </label>
                <textarea
                  id="room-form-desc"
                  className="form-control"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                {t('roomForm.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('roomForm.saving') : t('roomForm.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
